/**
 * The mailbox's folders (§17A.1) — which ones exist, what they are called, and
 * which one the screen opens on.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A HAND-KEPT SUBSET OF `MailFolder` IN `schema.prisma`, AND THE SUBSETTING IS
 * THE POINT RATHER THAN AN OVERSIGHT.
 *
 * The enum has SIX values; five are here. `ARCHIVE` is missing because nothing
 * in this build can put a message into it — archiving is an IMAP verb, and there
 * is no IMAP sync (see {@link useMailbox}). Offering a sixth folder that is
 * empty for a reason nobody can act on is worse than not offering it: the other
 * four are empty because mail has not arrived YET, which is a sentence the
 * screen can honestly say, and this one would be empty for ever.
 *
 * THE VALUES ARE THE ENUM'S OWN SPELLING, UPPERCASE, and they travel unchanged
 * from the control to the URL to the API's `?folder=` to the `WHERE`. A prettier
 * lowercase URL would need a translation table at both ends, which is one more
 * place for "sent" and "SENT" to disagree.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE ORDER IS THE ONE THE OWNER CHOSE (`sidebar-09`): Inbox, Drafts, Sent,
 * Junk, Trash. It is deliberately NOT re-sorted to put the only folder with
 * content first — a mailbox whose folders are in an order no other mail client
 * uses is a mailbox people stop trusting to be a mailbox.
 */
export const MAIL_FOLDERS = ["INBOX", "DRAFTS", "SENT", "SPAM", "TRASH"] as const;

/** One folder. A typo is a compile error, exactly as with `Capability`. */
export type MailFolder = (typeof MAIL_FOLDERS)[number];

/**
 * Where the mailbox opens.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `SENT`, NOT `INBOX`, AND THIS IS AN HONESTY DECISION RATHER THAN A DEFAULT.
 *
 * Every real mail client opens on the inbox, so opening on Sent is a deviation
 * that needs an argument. It is this: nothing in SteelBill fetches incoming mail
 * (§17A.1's IMAP half is unbuilt), so the inbox is empty and will stay empty
 * however long the owner waits. Landing him there would make his first
 * impression of the feature a blank pane — which reads as broken software, not
 * as unbuilt software, and no amount of explanatory copy fully undoes a first
 * impression.
 *
 * Sent is also the folder that answers the question he actually has. He shares a
 * quotation and wants to know weeks later whether it went and to which address;
 * that is what this screen is FOR. When an IMAP sync lands, this constant moves
 * to `INBOX` in one line and nothing else changes.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const DEFAULT_MAIL_FOLDER: MailFolder = "SENT";

/**
 * The folder named in a URL, or {@link DEFAULT_MAIL_FOLDER}.
 *
 * DENY-BY-DEFAULT IN THE SAME SPIRIT AS `can()`: `?folder=` carries whatever
 * somebody typed or whatever a stale bookmark holds, so an unrecognised value
 * falls back rather than being passed to the API. Without this the screen would
 * ask the server for folder `"drop table"` and render its 400 as a failure the
 * reader cannot act on.
 *
 * The comparison is case-SENSITIVE on purpose. The one spelling that exists is
 * the enum's; accepting `sent` as well would mean two URLs for one screen and
 * two cache keys for one list.
 */
export function parseMailFolder(value: string | null | undefined): MailFolder {
  if (typeof value !== "string") return DEFAULT_MAIL_FOLDER;
  return (MAIL_FOLDERS as readonly string[]).includes(value)
    ? (value as MailFolder)
    : DEFAULT_MAIL_FOLDER;
}

/**
 * The translation key for each folder's name.
 *
 * A `Record` with no default, so a sixth folder added to {@link MAIL_FOLDERS}
 * fails the typecheck until somebody writes its two strings — the construction
 * `ATTACHMENT_ABOUT_KEY` uses in the messenger, and for the same reason: a
 * missing key does not throw, it renders `mail.folder.archive` at the owner.
 */
export const MAIL_FOLDER_LABEL_KEY: Record<MailFolder, string> = {
  INBOX: "mail.folder.inbox",
  DRAFTS: "mail.folder.drafts",
  /*
   * "Junk", not "Spam". The enum column is `SPAM` because that is the IMAP
   * folder name, and the LABEL is the word the owner's other mail clients use —
   * which is also the word `sidebar-09` used. The two are allowed to differ:
   * one is a database value, the other is a sentence.
   */
  SENT: "mail.folder.sent",
  SPAM: "mail.folder.junk",
  TRASH: "mail.folder.trash",
};

/**
 * Does this folder receive anything in the build that is deployed today?
 *
 * ONLY `SENT` DOES, and the screen needs to know which case it is in so that an
 * empty folder can say WHY it is empty. "Nothing has been emailed yet" is true
 * of Sent and is a state the owner can change by pressing Send; it would be a
 * lie in the Inbox, where nothing will ever appear until an IMAP sync is built.
 * Telling him to wait for mail that cannot arrive is the failure this
 * distinction exists to prevent — and when the sync lands, this function goes
 * and both empty states collapse into one.
 */
export function folderCanFillToday(folder: MailFolder): boolean {
  return folder === "SENT";
}
